import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { fetchProspectById } from '../api/prospects';
import { fetchCallsByProspect } from '../api/calls';

const outcomeLabels = {
  success: 'Succès',
  'no-answer': 'Pas de réponse',
  voicemail: 'Messagerie',
  callback: 'Rappel requis',
  'not-interested': 'Pas intéressé',
  'wrong-number': 'Mauvais numéro',
};

const getOutcomeColor = (outcome) => {
  const colors = {
    success: 'text-success',
    'no-answer': 'text-warning',
    voicemail: 'text-secondary',
    callback: 'text-accent',
    'not-interested': 'text-error',
    'wrong-number': 'text-muted-foreground',
  };
  return colors?.[outcome] || 'text-muted-foreground';
};

const getStatusLabel = (status) => {
  const map = { prospect: 'Prospect', qualifie: 'Qualifié', proposition: 'Proposition', negociation: 'Négociation', conclu: 'Conclu', non_interesse: 'Non intéressé' };
  return map?.[status] || status;
};

const getPriorityLabel = (priority) => {
  const map = { high: 'Haute', medium: 'Moyenne', low: 'Basse' };
  return map?.[priority] || priority;
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date?.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds) => {
  const mins = Math.floor((seconds || 0) / 60);
  const secs = (seconds || 0) % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Modal prospect identique à celle de l'annuaire : infos + historique des appels et CR. En lecture seule. */
const ProspectDetailModal = ({ prospectId, onClose }) => {
  const [contact, setContact] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    if (!prospectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchProspectById(prospectId);
        const p = res?.data?.prospect;
        if (p) {
          setContact({
            id: p._id,
            name: p.fullName,
            fullName: p.fullName,
            company: p.company,
            position: p.position || '',
            phone: p.phone || '',
            mobilePhone: p.mobilePhone || '',
            email: p.email || '',
            status: p.status,
            priority: p.priority,
            lastContact: p.callbackAt ? `Rappel le ${new Date(p.callbackAt).toLocaleDateString('fr-FR')}` : 'Aucun rappel programmé',
            claimedByEmail: p.assignedTo?.email || null,
            website: p.website || '',
            ape: p.ape || '',
            siret: p.siret || '',
            address: p.address || '',
            postalCode: p.postalCode || '',
            city: p.city || '',
            notes: p.notes || '',
            tags: Array.isArray(p.tags) ? p.tags.map((t) => t?.name).filter(Boolean) : [],
            industry: p.company || 'N/A',
          });
        } else {
          setContact(null);
        }
      } catch (e) {
        console.error(e);
        setContact(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [prospectId]);

  useEffect(() => {
    if (!contact?.id) return;
    const loadCalls = async () => {
      setLoadingCalls(true);
      try {
        const res = await fetchCallsByProspect(contact.id);
        setCallHistory(res?.data?.calls || []);
      } catch (e) {
        console.error(e);
        setCallHistory([]);
      } finally {
        setLoadingCalls(false);
      }
    };
    loadCalls();
  }, [contact?.id]);

  if (!prospectId) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    >
      <div
        className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {loading ? 'Chargement...' : contact?.name || 'Prospect'}
            </h2>
            {contact && (
              <p className="text-xs text-muted-foreground">
                {getStatusLabel(contact?.status)} • {getPriorityLabel(contact?.priority)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Chargement...</div>
        ) : !contact ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Prospect introuvable.</div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={14} className="text-muted-foreground" />
                <span>{contact?.phone}</span>
              </div>
              {contact?.mobilePhone && (
                <div className="flex items-center gap-2">
                  <Icon name="Smartphone" size={14} className="text-muted-foreground" />
                  <span>{contact?.mobilePhone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span>{contact?.email}</span>
              </div>
              {contact?.website && (
                <div className="flex items-center gap-2">
                  <Icon name="Globe2" size={14} className="text-muted-foreground" />
                  <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                    {contact.website}
                  </a>
                </div>
              )}
              {contact?.ape && (
                <div className="flex items-center gap-2">
                  <Icon name="Building2" size={14} className="text-muted-foreground" />
                  <span>Code APE : {contact.ape}</span>
                </div>
              )}
              {contact?.siret && (
                <div className="flex items-center gap-2">
                  <Icon name="Hash" size={14} className="text-muted-foreground" />
                  <span>SIRET : {contact.siret}</span>
                </div>
              )}
              {(contact?.address || contact?.postalCode || contact?.city) && (
                <div className="flex items-start gap-2">
                  <Icon name="MapPin" size={14} className="text-muted-foreground mt-0.5" />
                  <div className="text-xs text-foreground space-y-0.5">
                    {contact?.address && <div>{contact.address}</div>}
                    {(contact?.postalCode || contact?.city) && (
                      <div>{contact.postalCode} {contact.city}</div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span>{contact?.lastContact}</span>
              </div>
              {contact?.claimedByEmail && (
                <div className="flex items-center gap-2">
                  <Icon name="UserCheck" size={14} className="text-muted-foreground" />
                  <span>Assigné à {contact?.claimedByEmail}</span>
                </div>
              )}
              <div className="mt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Tags</p>
                {Array.isArray(contact?.tags) && contact.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {contact.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun tag assigné</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-foreground whitespace-pre-line">
                {contact?.notes || 'Aucune note pour le moment.'}
              </p>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-foreground">Historique des appels</h3>
                {loadingCalls && <span className="text-xs text-muted-foreground">Chargement...</span>}
              </div>
              {callHistory.length === 0 && !loadingCalls ? (
                <p className="text-xs text-muted-foreground">Aucun appel enregistré pour ce prospect.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">Date & heure</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">Durée</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">Résultat</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">Agent</th>
                        <th className="px-3 py-2 text-right font-medium text-muted-foreground uppercase tracking-wider">CR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {callHistory.map((call) => (
                        <tr key={call._id}>
                          <td className="px-3 py-2"><span className="data-text">{formatTimestamp(call.startCallAt)}</span></td>
                          <td className="px-3 py-2"><span className="data-text">{formatDuration(call.timeDuration)}</span></td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${getOutcomeColor(call.status)}`}>
                              {outcomeLabels[call.status] || call.status}
                            </span>
                          </td>
                          <td className="px-3 py-2"><span className="text-foreground">{call.user?.email || 'N/A'}</span></td>
                          <td className="px-3 py-2 text-right">
                            {call.cr && call.cr.trim() !== '' ? (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Voir le compte rendu"
                                onClick={() => setSelectedCall(call)}
                              >
                                <Icon name="Eye" size={14} className="text-primary" />
                              </button>
                            ) : (
                              <span className="inline-flex items-center justify-center p-1.5 rounded" title="Aucun compte rendu">
                                <Icon name="XCircle" size={14} className="text-error" />
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {selectedCall && (
                <div className="mt-4 border border-border rounded-lg p-3 bg-muted/40">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-xs font-semibold text-foreground">Compte rendu de l&apos;appel</p>
                      <p className="text-[11px] text-muted-foreground">
                        {formatTimestamp(selectedCall.startCallAt)} • {selectedCall.user?.email || 'N/A'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-primary hover:text-primary/80 font-medium"
                      onClick={() => setSelectedCall(null)}
                    >
                      Revenir à l&apos;historique
                    </button>
                  </div>
                  <p className="text-xs text-foreground whitespace-pre-wrap">{selectedCall.cr}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProspectDetailModal;
